import Plotly, { Layout } from "plotly.js/dist/plotly-myPlotly.js";
import React, { useEffect } from "react";
import { bodyFontFamily, chartFont } from "../../shared/global";
import { Merge } from "../../shared/TypeScriptHelpers";
import { ChartProps } from "./ChartHOC";
import type { Legend } from "./COREChartLegend";
import { COREChartLegend } from "./COREChartLegend";
import { grey100, prepareChartColor } from "../Content/COREColour";
import {
  AggregatesChartProps,
  Trace,
} from "../../modules/chartBuilder/AggregatesChartV2";
import { ChartDataType, COREChartProps } from "./COREChart";
import equal from "fast-deep-equal";
import { downloadCSVFile } from "./ChartUtils/csvUtils";
import { generateChartLegends } from "./ChartUtils/chartLegendHelpers";

export type BasicTrace = Pick<
  Trace,
  | "name"
  | "line.color"
  | "line.dash"
  | "line.width"
  | "line.shape"
  | "line.smoothing"
  | "type"
  | "hovertemplate"
  | "marker"
> & {
  x: string[];
  y: number[];
  hoverlabel?: Partial<Plotly.PlotData["hoverlabel"]>;
  mode?: Plotly.PlotData["mode"];
  yaxis?: string;
  line?: Omit<Partial<Plotly.PlotData["line"]>, "color"> & { color?: string };
  "line.dash"?: string;
  fill?: Plotly.PlotData["fill"];
  stackgroup?: string;
  fillcolor?: string;
  legendgroup?: string;
  showlegend?: boolean;
  marker?: {
    color?: string;
    size?: number | number[];
    line?: { color?: string; width?: number };
  };
  // Overrides the label shown in CORETimeSeriesChartTooltip without affecting
  // `name` (which drives legend grouping/dedup). Falls back to `name` when unset.
  tooltipLabel?: string;
};

export type COREBasicChartProps = Merge<
  Pick<
    AggregatesChartProps,
    | "downloadPng"
    | "downloadCsv"
    | "testID"
    | "plotRef"
    | "chartPanel"
    | "chartTitle"
    | "chartLegendRef"
    | "empty"
    | "setProgressPercent"
    | "showlegend"
    | "chartHoverMode"
  >,
  {
    isWidget?: boolean;
    fitParent?: boolean;
    layout?: Partial<Layout>;
    traces: BasicTrace[];
    xAxisLabel?: string;
    yAxisLabel?: string;
    customLayout?: boolean;
    onPlotlyHover?: (event: Plotly.PlotMouseEvent) => void;
    onPlotlyUnhover?: () => void;
    onPlotlyClick?: (event: Plotly.PlotMouseEvent) => void;
    customTooltip?: COREChartProps["customTooltip"];
    interactiveTooltip?: COREChartProps["interactiveTooltip"];
    legends?: Legend[];
    onLegendsChange?: (legends: Legend[]) => void;
    /** Legend in the exported PNG. Defaults to `showlegend`, or to always-on
     *  for a chart with controlled legends. See `COREChartLegend`. */
    showlegendInExport?: boolean;
    allowEmptyChart?: boolean;
    transformCsvData?: (traces: BasicTrace[]) => string[][];
  }
>;

const generateChartLayout = (
  layout: Partial<Plotly.Layout> | undefined,
  chartTitle: COREBasicChartProps["chartTitle"],
  xAxisLabel: COREBasicChartProps["xAxisLabel"],
  yAxisLabel: COREBasicChartProps["yAxisLabel"],
  chartHoverMode: COREBasicChartProps["chartHoverMode"]
): Partial<Layout> & {
  filename?: string;
} => {
  return {
    font: {
      family: chartFont,
    },
    filename: chartTitle,
    titlefont: {
      family: bodyFontFamily,
    },
    margin: {
      t: 30,
      r: 2,
    },
    barmode: "stack",
    hovermode: chartHoverMode,
    showlegend: false,
    xaxis: {
      showline: false,
      automargin: true,
      title: {
        text: xAxisLabel,
        standoff: 15,
        font: {
          family: chartFont,
        },
      },
      linecolor: grey100,
    },
    yaxis: {
      linecolor: "#F2F2F2",
      showline: false,
      showgrid: true,
      title: {
        text: yAxisLabel,
        standoff: 15,
        font: {
          family: chartFont,
        },
      },
    },
    ...layout,
  };
};

export const BasicChart: React.FC<COREBasicChartProps & ChartProps> = ({
  traces,
  testID,
  plotRef,
  chartTitle,
  xAxisLabel,
  yAxisLabel,
  chartPanel,
  downloadCsv,
  downloadPng,
  setOnDownloadCSV,
  setOnDownloadPNG,
  setOnResetChart,
  layout,
  chartLegendRef,
  showlegend = true,
  empty,
  setProgressPercent,
  isWidget,
  fitParent,
  chartHoverMode = "closest",
  customLayout,
  onPlotlyHover,
  onPlotlyUnhover,
  onPlotlyClick,
  customTooltip,
  interactiveTooltip,
  legends: controlledLegends,
  onLegendsChange,
  showlegendInExport,
  allowEmptyChart,
  transformCsvData,
}) => {
  const tracesWithColor: BasicTrace[] = prepareChartColor(traces);
  const generatedLegends: Legend[] = generateChartLegends(
    tracesWithColor.filter((t) => t.showlegend !== false)
  );
  const legends = controlledLegends ?? generatedLegends;
  const chartLayout = generateChartLayout(
    layout,
    chartTitle,
    xAxisLabel,
    yAxisLabel,
    chartHoverMode
  );

  const newTraces: Partial<ChartDataType>[] = tracesWithColor.map((item) => {
    return {
      ...item,
      marker: {
        ...item.marker,
        color: item["line.color"],
        ...(item.marker?.size && { size: item.marker?.size }),
      },
      line: {
        ...(item["line.color"] && { color: item["line.color"] }),
        ...(item["line.dash"] && { dash: item["line.dash"] }),
        ...(item["line.width"] && { width: item["line.width"] }),
        ...(item["line.shape"] && { shape: item["line.shape"] }),
        ...(item["line.smoothing"] && { smoothing: item["line.smoothing"] }),
      },
    };
  });

  setProgressPercent && setProgressPercent(1);
  useEffect(() => {
    const handleResetChart = () => {
      if (!plotRef?.current) {
        return;
      }
      // Reset every axis present in the layout (xaxis, yaxis, yaxis2, ...)
      const currentLayout = plotRef.current.props.layout as Record<
        string,
        unknown
      >;
      const resetLayout: Record<string, unknown> = {};
      Object.keys(currentLayout).forEach((key) => {
        if (/^[xy]axis\d*$/.test(key)) {
          resetLayout[key] = {
            ...(currentLayout[key] as object),
            autorange: true,
          };
        }
      });
      Plotly.relayout(plotRef.current.el, resetLayout);
    };

    const handleDownloadPNG = () => {
      chartLegendRef?.current?.callDrawImage();
    };

    const handleDownloadCSV = () => {
      if (!plotRef?.current) {
        return;
      }
      if (transformCsvData) {
        downloadCSVFile(transformCsvData(traces), true, chartTitle ?? "");
      } else {
        if (!plotRef.current.props.layout.filename) {
          plotRef.current.props.layout.filename = chartTitle;
        }
        downloadCSVFile(plotRef.current.el);
      }
    };

    if (setOnResetChart) setOnResetChart(() => handleResetChart);
    if (setOnDownloadPNG) setOnDownloadPNG(() => handleDownloadPNG);
    if (setOnDownloadCSV) setOnDownloadCSV(() => handleDownloadCSV);
  }, [
    chartLegendRef,
    chartTitle,
    plotRef,
    setOnDownloadCSV,
    setOnDownloadPNG,
    setOnResetChart,
    transformCsvData,
    traces,
  ]);

  return (
    <COREChartLegend
      chartId={testID}
      chartTitle={chartTitle}
      chartPanel={chartPanel}
      downloadCsv={downloadCsv ?? false}
      downloadPng={downloadPng}
      data={newTraces}
      layout={chartLayout}
      testID={testID}
      legends={legends}
      showlegend={showlegend}
      legendFilter={true}
      plotRef={plotRef}
      ref={chartLegendRef}
      empty={empty}
      isWidget={isWidget}
      fitParent={fitParent}
      customLayout={customLayout}
      onPlotlyHover={onPlotlyHover}
      onPlotlyUnhover={onPlotlyUnhover}
      onPlotlyClick={onPlotlyClick}
      customTooltip={customTooltip}
      interactiveTooltip={interactiveTooltip}
      onLegendsChange={onLegendsChange}
      showlegendInExport={showlegendInExport}
      allowEmptyChart={allowEmptyChart}
    />
  );
};

export const COREBasicChart = React.memo<
  React.ComponentType<
    COREBasicChartProps & ChartProps & { ref?: React.ForwardedRef<any> }
  >
>(
  BasicChart,
  (prevProps, nextProps) =>
    prevProps &&
    // Switching in and out of full screen changes nothing else about the chart,
    // so without this the mode the chart is drawn in never reaches Plotly. The
    // other charts compare it for the same reason (see CORERadarChart).
    prevProps.fitParent === nextProps.fitParent &&
    equal(prevProps.layout, nextProps.layout) &&
    equal(prevProps.traces, nextProps.traces) &&
    equal(prevProps.legends, nextProps.legends) &&
    equal(prevProps.setOnDownloadCSV, nextProps.setOnDownloadCSV) &&
    equal(prevProps.setOnDownloadPNG, nextProps.setOnDownloadPNG) &&
    equal(prevProps.setOnResetChart, nextProps.setOnResetChart)
);
